import { useEffect, useRef, useState } from "react";
import { BookImage, Camera, Link2, Loader2, Pencil, Plus, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Trip Photo Journal — day-by-day photos and notes that become a shareable trip
   memory. Entries live in /api/journal; a public read-only link (/memories/{token})
   can be created and revoked at any time. */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(`${d}T12:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};
const dayNumber = (date, start) => {
  if (!date || !start) return null;
  const n = Math.round((new Date(`${date}T12:00:00`) - new Date(`${start}T12:00:00`)) / 86400000) + 1;
  return n >= 1 ? n : null;
};

export default function TripJournal({ tripId, tripStartDate, readOnly }) {
  const [doc, setDoc] = useState(null);
  const [adding, setAdding] = useState(false);
  const token = localStorage.getItem("token");

  const load = () => api.get(`/journal/trip/${tripId}`).then((r) => setDoc(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!doc) return null;
  const entries = doc.entries || [];

  const enableShare = async () => {
    try {
      const { data } = await api.post(`/journal/trip/${tripId}/share`);
      setDoc((d) => ({ ...d, share_token: data.share_token }));
      toast.success("Shareable memory link created — anyone with the link can view it");
    } catch { toast.error("Could not create the share link"); }
  };
  const disableShare = async () => {
    try {
      await api.delete(`/journal/trip/${tripId}/share`);
      setDoc((d) => ({ ...d, share_token: null }));
      toast.success("Sharing stopped — the old link no longer works");
    } catch { toast.error("Could not stop sharing"); }
  };
  const shareUrl = doc.share_token ? `${window.location.origin}/memories/${doc.share_token}` : null;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <section className="mb-10" data-testid="trip-journal">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-heading font-700 text-xl flex items-center gap-2">
          <BookImage className="w-5 h-5 text-[#7c3aed]" /> Trip Photo Journal
        </h2>
        {!readOnly && (
          <button onClick={() => setAdding((a) => !a)} data-testid="journal-add-entry-btn"
            className="inline-flex items-center gap-1.5 border border-[#7c3aed] text-[#7c3aed] px-3 py-2 text-sm font-700 hover:bg-violet-50">
            <Plus className="w-4 h-4" /> Add a Day
          </button>
        )}
      </div>

      {!readOnly && (
        <div className="border border-zinc-200 bg-zinc-50 px-4 py-3 mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="journal-share-bar">
          <Share2 className="w-4 h-4 text-[#7c3aed] shrink-0" />
          {shareUrl ? (
            <>
              <span className="font-600">Shared as a trip memory:</span>
              <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="journal-share-link"
                className="font-mono text-xs text-[#7c3aed] hover:underline break-all">{shareUrl}</a>
              <button onClick={copyLink} data-testid="journal-copy-link" className="inline-flex items-center gap-1 text-xs font-700 text-[#7c3aed] hover:underline"><Link2 className="w-3.5 h-3.5" /> Copy</button>
              <button onClick={disableShare} data-testid="journal-stop-share" className="text-xs font-700 text-rose-600 hover:underline">Stop sharing</button>
            </>
          ) : (
            <>
              <span className="font-600">Turn this journal into a shareable trip memory anyone can view.</span>
              <button onClick={enableShare} data-testid="journal-enable-share"
                className="inline-flex items-center gap-1 bg-[#7c3aed] text-white px-3 py-1.5 text-xs font-700 hover:bg-violet-700">
                <Share2 className="w-3.5 h-3.5" /> Create memory link
              </button>
            </>
          )}
        </div>
      )}

      {adding && <EntryForm tripStartDate={tripStartDate} onCancel={() => setAdding(false)}
        onSave={async (form) => {
          try { await api.post(`/journal/trip/${tripId}/entries`, form); setAdding(false); load(); toast.success("Journal entry added"); }
          catch (e) { toast.error(e?.response?.data?.detail || "Could not save the entry"); }
        }} />}

      {entries.length === 0 && !adding && (
        <p className="border border-zinc-300 bg-white p-4 text-sm text-muted-foreground" data-testid="journal-empty">
          No memories yet — add day-by-day photos and notes, then share the whole journal as one link.
        </p>
      )}

      <div className="space-y-4">
        {entries.map((e) => (
          <Entry key={e.id} entry={e} token={token} readOnly={readOnly} tripStartDate={tripStartDate} onChange={load} />
        ))}
      </div>
    </section>
  );
}

function EntryForm({ initial, tripStartDate, onSave, onCancel }) {
  const [date, setDate] = useState(initial?.date || tripStartDate || new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState(initial?.title || "");
  const [text, setText] = useState(initial?.text || "");
  return (
    <div className="border border-[#7c3aed]/40 bg-violet-50/40 p-4 mb-4 space-y-2" data-testid="journal-entry-form">
      <div className="flex flex-wrap gap-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="journal-form-date"
          className="border border-zinc-300 bg-white px-2 py-1.5 text-sm" />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Day title — e.g. Louvre & sunset picnic"
          data-testid="journal-form-title" className="flex-1 min-w-[200px] border border-zinc-300 bg-white px-3 py-1.5 text-sm" />
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="What happened today? Favorite moments, food, funny bits…"
        data-testid="journal-form-text" className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm" />
      <div className="flex gap-2">
        <button onClick={() => onSave({ date, title, text })} data-testid="journal-form-save"
          className="bg-[#7c3aed] text-white px-4 py-1.5 text-sm font-700 hover:bg-violet-700">Save</button>
        <button onClick={onCancel} data-testid="journal-form-cancel" className="border border-zinc-300 px-4 py-1.5 text-sm font-700">Cancel</button>
      </div>
    </div>
  );
}

function Entry({ entry, token, readOnly, tripStartDate, onChange }) {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const day = dayNumber(entry.date, tripStartDate);

  const remove = async () => {
    if (!window.confirm("Delete this journal day (and its photos)?")) return;
    try { await api.delete(`/journal/entries/${entry.id}`); onChange(); } catch { toast.error("Could not delete"); }
  };
  const removePhoto = async (pid) => {
    try { await api.delete(`/journal/photos/${pid}`); onChange(); } catch { toast.error("Could not delete photo"); }
  };
  const [photoNote, setPhotoNote] = useState("");
  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        if (photoNote.trim()) fd.append("caption", photoNote.trim());
        await api.post(`/journal/entries/${entry.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onChange();
      setPhotoNote("");
      toast.success(files.length > 1 ? `${files.length} photos added` : "Photo added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload the photo"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (editing) return <EntryForm initial={entry} onCancel={() => setEditing(false)}
    onSave={async (form) => {
      try { await api.put(`/journal/entries/${entry.id}`, form); setEditing(false); onChange(); toast.success("Entry updated"); }
      catch { toast.error("Could not update"); }
    }} />;

  return (
    <div className="border border-zinc-300 bg-white" data-testid={`journal-entry-${entry.id}`}>
      <div className="px-4 py-3 flex items-start justify-between gap-2 border-b border-zinc-100">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-[#7c3aed] font-700">
            {day ? `Day ${day} · ` : ""}{fmtDate(entry.date)}
          </div>
          {entry.title && <div className="font-heading font-900 text-lg mt-0.5">{entry.title}</div>}
        </div>
        {!readOnly && (
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditing(true)} data-testid={`journal-edit-${entry.id}`}
              className="p-1.5 text-zinc-400 hover:text-[#7c3aed]"><Pencil className="w-4 h-4" /></button>
            <button onClick={remove} data-testid={`journal-delete-${entry.id}`}
              className="p-1.5 text-zinc-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div className="p-4">
        {entry.text && <p className="text-sm text-zinc-700 whitespace-pre-wrap mb-3">{entry.text}</p>}
        <div className="flex flex-wrap gap-2">
          {(entry.photos || []).map((p) => (
            <div key={p.id} className="relative group">
              <img src={`${BACKEND}${p.url}?auth=${token}`} alt={p.caption || entry.title || "Trip memory"} loading="lazy"
                data-testid={`journal-photo-${p.id}`} title={p.caption || ""}
                className="w-28 h-28 sm:w-36 sm:h-36 object-cover border border-zinc-200" />
              {p.caption && <p className="text-[10px] text-muted-foreground w-28 sm:w-36 truncate" title={p.caption}>{p.caption}</p>}
              {!readOnly && (
                <button onClick={() => removePhoto(p.id)} data-testid={`journal-photo-delete-${p.id}`}
                  className="absolute top-1 right-1 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`journal-upload-${entry.id}`}
              className="w-28 h-28 sm:w-36 sm:h-36 border-2 border-dashed border-zinc-300 text-zinc-400 hover:border-[#7c3aed] hover:text-[#7c3aed] flex flex-col items-center justify-center gap-1 text-xs font-700">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              {uploading ? "Uploading…" : "Add photos"}
            </button>
          )}
        </div>
        {!readOnly && (
          <>
            <input value={photoNote} onChange={(e) => setPhotoNote(e.target.value)} placeholder="Photo title / note (optional) — applies to the next photos you add…"
              data-testid={`journal-photo-note-${entry.id}`}
              className="mt-2 w-full border border-zinc-300 px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#7c3aed]" />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
              onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          </>
        )}
      </div>
    </div>
  );
}
